/**
 * SubscriptionWall — Shown when user is authenticated but not subscribed.
 *
 * Overlays the ML Trading page with a subscribe CTA.
 * User can still see blurred/limited data behind it.
 */

import React, { useState } from 'react';
import useAuth from '../../hooks/useAuth.jsx';
import './SubscriptionWall.css';

const AUTH_WORKER_URL = import.meta.env.VITE_AUTH_WORKER_URL || 'https://vectrax-auth.thegpc806.workers.dev';

export default function SubscriptionWall() {
  const { user, getIdToken, refreshSubscription } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubscribe = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getIdToken();
      const resp = await fetch(`${AUTH_WORKER_URL}/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || 'Checkout failed');
      }

      const { subscriptionId, razorpayKeyId } = await resp.json();

      // Load Razorpay checkout
      const options = {
        key: razorpayKeyId,
        subscription_id: subscriptionId,
        name: 'Vectrax',
        description: 'ML Trading Pro — Monthly',
        handler: async () => {
          // Payment successful — refresh subscription status
          await refreshSubscription();
        },
        prefill: {
          email: user.email,
          name: user.displayName || '',
        },
        theme: {
          color: '#4a6cf7',
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="subscription-wall">
      <div className="subscription-wall-content">
        <h2>Unlock ML Trading Pro</h2>
        <p>Get real-time ML-powered trading signals</p>

        <ul className="subscription-features">
          <li>Real-time BUY/SELL signals for TQQQ, UPRO, SQQQ, SPXU</li>
          <li>ML confidence scores and key features</li>
          <li>Stop-loss and take-profit levels</li>
          <li>Full transaction history and performance metrics</li>
          <li>Multi-algorithm ensemble (V1C, V2C)</li>
        </ul>

        <button
          className="subscribe-btn"
          onClick={handleSubscribe}
          disabled={loading}
        >
          {loading ? 'Processing...' : 'Subscribe Now'}
        </button>

        {error && <p className="subscription-error">{error}</p>}

        <p className="subscription-disclaimer">
          Cancel anytime. Signals are for informational purposes only.
        </p>
      </div>
    </div>
  );
}
